import {
    ActionUpdate,
    APIExistingFluidInstruction,
    ExistingCriInstruction,
    ExistingDiagInstruction,
    ExistingFluidInstruction,
    ExistingMedInstruction,
    ExistingTaskInstruction,
    ExpandedPatient,
    InstructionAction,
    isInstanceOfAPIExistingFluidInstruction,
    isInstanceOfAPIExistingCriInstruction,
    OutgoingAction,
    OutgoingOffSchedAction,
    backEndFluidToFrontendFluid,
    backEndCriToFrontendCri,
    APIExistingCriInstruction,
    isInstanceOfMedicineInstruction,
    convertToAPI,
    convertInstructionSearchOptionToLocal,
    convertInstructionItemToLocal,
    convertMainTsItemToLocal,
    convertPackageInstructionItemToLocal,
    ExistingToGoMedInstruction,
    VCPExternalResult,
    FrequencyUpdate,
    INSTRUCTION_TYPES,
    ExistingOxygenTherapyInstruction,
    FrequencyUpdateMultiple,
} from 'utils/dataTypes';
import { TagType } from 'utils/types/enums';
import { MainTsData } from 'utils/types/MainTreatmentSheetTypes';
import {
    BaseSearchOption,
    EnrichedPackageSearchOption,
    FluidSearchOption,
    OutgoingCriOrder,
    OutgoingDiagOrder,
    OutgoingTaskOrder,
    OutgoingFluidOrder,
    OutgoingMedicineOrder,
    APIFluidSearchOption,
    isInstanceOfAPIFluidOption,
    backendFluidOptionToFrontendFluidOption,
    isInstanceOfAPIPackageFluidInstruction,
    backendPackageFluidInstructionToFrontendPackageFluidInstruction,
    isInstanceOfAPIPackageCriInstruction,
    backendPackageCriInstructionToFrontendPackageCriInstruction,
    isInstanceOfMedicineSearchOption,
    isInstanceOfPackageMedInstruction,
    IHDResultsFile,
    AdditiveSearchOption,
    OutgoingFluidAdditiveOrder,
    OutgoingOxygenTherapyOrder,
    DiagnosticSearchOptionWithRefLabs,
} from '../utils/types/InstructionOrderTypes';
import { instructionApi } from './instructionApi';
import moment from 'moment';
import _ from 'lodash';

function _resolveInstructionPath(typeId: string) {
    if (typeId === 'C') {
        return 'cri';
    }

    if (typeId === 'D') {
        return 'diagnostic';
    }

    if (typeId === 'F' || typeId === 'OT') {
        return 'fluids';
    }

    if (typeId === 'M') {
        return 'medication';
    }

    if (typeId === 'T') {
        return 'task';
    }

    if (typeId === 'TGH') {
        return 'togomed';
    }
}

// Define a service using a base URL and expected endpoints
export const instructionService = instructionApi.injectEndpoints({
    endpoints: (builder) => ({
        getInstructionOptions: builder.query<BaseSearchOption[], null>({
            query: () => `search`,
            transformResponse: (response: {
                data: (BaseSearchOption | APIFluidSearchOption)[];
            }): (BaseSearchOption | FluidSearchOption)[] => {
                const values: (BaseSearchOption | APIFluidSearchOption)[] = response.data.map((item) => {
                    if (isInstanceOfAPIFluidOption(item)) {
                        return backendFluidOptionToFrontendFluidOption(item);
                    } else if (isInstanceOfMedicineSearchOption(item)) {
                        return convertInstructionSearchOptionToLocal(item);
                    } else {
                        return item;
                    }
                });
                return values;
            },
        }),
        getMainTreatmentSheet: builder.query<MainTsData[], null>({
            query: () => `master_treatment_sheet`,
            providesTags: (result, error, id) => [{ type: TagType.MainTs }],
            transformResponse: (response: { data: MainTsData[] }) => {
                const values: MainTsData[] = response.data.map((row) => {
                    const { instructions, ...restofRow } = row;
                    const convertedInstructions = instructions.map((item) => {
                        if (isInstanceOfAPIExistingFluidInstruction(item)) {
                            return backEndFluidToFrontendFluid(item);
                        } else if (isInstanceOfAPIExistingCriInstruction(item)) {
                            return convertMainTsItemToLocal(backEndCriToFrontendCri(item));
                        } else if (isInstanceOfMedicineInstruction(item)) {
                            return convertMainTsItemToLocal(item);
                        } else {
                            return item;
                        }
                    });
                    return { instructions: convertedInstructions, ...restofRow };
                });
                return values;
            },
        }),
        getInstructionsByVisitId: builder.query<
            (
                | ExistingMedInstruction
                | ExistingDiagInstruction
                | ExistingTaskInstruction
                | ExistingFluidInstruction
                | ExistingCriInstruction
                | ExistingToGoMedInstruction
                | ExistingOxygenTherapyInstruction
            )[],
            {
                visitId: number;
                isRefresh?: boolean;
            }
        >({
            query: ({ visitId, isRefresh }) => {
                if (isRefresh) {
                    const lastCheckTime = moment().subtract(2, 'minutes').unix();
                    return `visit/${visitId}/all?lastchecktime=${lastCheckTime}`;
                } else {
                    return `visit/${visitId}/all`;
                }
            },
            providesTags: (result, error, { visitId }) => [{ type: TagType.Instruction, id: visitId }],
            transformResponse: (response: {
                data: (
                    | ExistingMedInstruction
                    | ExistingDiagInstruction
                    | ExistingCriInstruction
                    | APIExistingFluidInstruction
                    | APIExistingCriInstruction
                    | ExistingToGoMedInstruction
                    | ExistingOxygenTherapyInstruction
                )[];
            }): (
                | ExistingMedInstruction
                | ExistingDiagInstruction
                | ExistingCriInstruction
                | ExistingFluidInstruction
                | ExistingToGoMedInstruction
                | ExistingOxygenTherapyInstruction
            )[] => {
                const values = response.data.map((item) => {
                    if (isInstanceOfAPIExistingFluidInstruction(item)) {
                        return backEndFluidToFrontendFluid(item);
                    } else if (isInstanceOfAPIExistingCriInstruction(item)) {
                        return convertInstructionItemToLocal(backEndCriToFrontendCri(item));
                    } else if (isInstanceOfMedicineInstruction(item)) {
                        return convertInstructionItemToLocal(item);
                    } else {
                        return item;
                    }
                });
                return values;
            },
        }),
        getFluidOptions: builder.query<FluidSearchOption[], null>({
            query: () => `/fluids`,
            transformResponse: (response: { data: APIFluidSearchOption[] }): FluidSearchOption[] => {
                const values: FluidSearchOption[] = response.data.map((item) => {
                    if (isInstanceOfAPIFluidOption(item)) {
                        return backendFluidOptionToFrontendFluidOption(item);
                    } else {
                        return item;
                    }
                });
                return values;
            },
        }),
        getAdditiveOptions: builder.query<AdditiveSearchOption[], null>({
            query: () => `/fluid_additives`,
            transformResponse: (response: { data: AdditiveSearchOption[] }) => {
                const sorted = _.sortBy(response.data, ['sort_rank']);
                return sorted;
            },
        }),
        getPackageDetails: builder.query<EnrichedPackageSearchOption, number>({
            query: (package_id) => `package/${package_id}`,
            providesTags: (result, error, id) => [{ type: 'Package', id }],
            transformResponse: (response: { data: EnrichedPackageSearchOption }) => {
                const { instructions, ...restofData } = response.data;
                const convertedInstructions = instructions.map((item) => {
                    if (isInstanceOfAPIPackageFluidInstruction(item)) {
                        return backendPackageFluidInstructionToFrontendPackageFluidInstruction(item);
                    }
                    // TODO: I could not test this. Currently there is no way to add a cri to a package in the admin panel.
                    else if (isInstanceOfAPIPackageCriInstruction(item)) {
                        return convertPackageInstructionItemToLocal(backendPackageCriInstructionToFrontendPackageCriInstruction(item));
                    } else if (isInstanceOfPackageMedInstruction(item)) {
                        return convertPackageInstructionItemToLocal(item);
                    } else {
                        return item;
                    }
                });
                return { instructions: convertedInstructions, ...restofData };
            },
        }),
        downloadIdexxResultFile: builder.query<IHDResultsFile, { actionId: number }>({
            query: ({ actionId }) => `/instruction/${actionId}/file`,
            providesTags: (result, error, { actionId }) => [{ type: TagType.ActionId, id: actionId }],
            transformResponse: (response: { data: IHDResultsFile }) => {
                return response.data;
            },
        }),
        getMedicationRoutes: builder.query<any, any>({
            query: () => `medications-routes`,
            transformResponse: (response: { data: any }) => {
                return response.data;
            },
        }),
        getNonMedicalWhyTypes: builder.query<any, any>({
            query: () => `non-medical-why-types`,
            transformResponse: (response: { data: any }) => {
                return response.data;
            },
        }),
        getIndividualInstruction: builder.query<
            | ExistingMedInstruction
            | ExistingDiagInstruction
            | ExistingTaskInstruction
            | ExistingFluidInstruction
            | ExistingCriInstruction
            | ExistingToGoMedInstruction
            | ExistingOxygenTherapyInstruction,
            {
                typeId: string;
                instructionId: number;
            }
        >({
            query: ({ typeId, instructionId }) => `instruction/${instructionId}/${_resolveInstructionPath(typeId)}`,
            transformResponse: (response: {
                data:
                    | ExistingMedInstruction
                    | ExistingDiagInstruction
                    | ExistingCriInstruction
                    | APIExistingFluidInstruction
                    | APIExistingCriInstruction
                    | ExistingToGoMedInstruction
                    | ExistingOxygenTherapyInstruction;
            }) => {
                if (isInstanceOfAPIExistingFluidInstruction(response.data)) {
                    return backEndFluidToFrontendFluid(response.data);
                } else if (isInstanceOfAPIExistingCriInstruction(response.data)) {
                    return convertInstructionItemToLocal(backEndCriToFrontendCri(response.data));
                } else if (isInstanceOfMedicineInstruction(response.data)) {
                    return convertInstructionItemToLocal(response.data);
                } else {
                    return response.data;
                }
            },
        }),
        getIdexxRecentResults: builder.query<Array<VCPExternalResult>, void>({
            query: () => 'idexx/ihd/recent',
            transformResponse: (response: { data: any }) => {
                return response.data.results.map((row: any) => {
                    const rowWithKey = row;
                    rowWithKey.key = rowWithKey.resultId;
                    return rowWithKey;
                }) as Array<VCPExternalResult>;
            },
        }),
        getWebpacsRecentResults: builder.query<Array<VCPExternalResult>, void>({
            query: () => 'webpacs/recent',
            transformResponse: (response: { data: any }) => {
                return response.data.results.map((row: any) => {
                    const rowWithKey = row;
                    rowWithKey.key = rowWithKey.resultId;
                    return rowWithKey;
                }) as Array<VCPExternalResult>;
            },
        }),

        /**
         * ADD/POST/NEW
         */
        postCheckinTprInstructions: builder.mutation<void, { visitId: number }>({
            query({ visitId }) {
                return {
                    url: `visit/${visitId}/checkin`,
                    method: 'POST',
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        setInitialWeight: builder.mutation<void, { visitId: number; weight: string | null }>({
            query({visitId, weight}) {
                return {
                    url: `visit/${visitId}/initial_weight`,
                    method: 'POST',
                    body: {weight: weight}
                }
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addMedInstruction: builder.mutation<ExistingMedInstruction, { visitId: number; body: OutgoingMedicineOrder }>({
            query({ visitId, body }) {
                const { dose, dose_unit } = convertToAPI(body.dose, body.dose_unit);
                const newBody = {
                    ...body,
                    dose,
                    dose_unit,
                };
                return {
                    url: `visit/${visitId}/med`,
                    method: 'POST',
                    body: newBody,
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addDiagInstruction: builder.mutation<ExistingDiagInstruction, { visitId: number; body: OutgoingDiagOrder }>({
            query({ visitId, body }) {
                return {
                    url: `visit/${visitId}/diagnostic`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addTaskInstruction: builder.mutation<ExistingTaskInstruction, { visitId: number; body: OutgoingTaskOrder }>({
            query({ visitId, body }) {
                return {
                    url: `visit/${visitId}/task`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addCriInstruction: builder.mutation<APIExistingCriInstruction, { visitId: number; body: OutgoingCriOrder }>({
            query({ visitId, body }) {
                if (body.dose_unit === 'mcg/kg/min') {
                    body.dose_unit = 'mg/kg/hr';
                    body.dose = (body.dose * 60) / 1000;
                }

                const { dose, dose_unit } = convertToAPI(body.dose, body.dose_unit);
                const newBody = {
                    ...body,
                    dose,
                    dose_unit,
                };
                return {
                    url: `visit/${visitId}/cri`,
                    method: 'POST',
                    body: newBody,
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addFluidInstruction: builder.mutation<APIExistingFluidInstruction, { visitId: number; body: OutgoingFluidOrder }>({
            query({ visitId, body }) {
                return {
                    url: `visit/${visitId}/fluids`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addOxygenTherapyInstruction: builder.mutation<void, { visitId: number; body: OutgoingOxygenTherapyOrder }>({
            query({ visitId, body }) {
                return {
                    url: `visit/${visitId}/oxygen_therapy`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        addFluidAdditive: builder.mutation<APIExistingFluidInstruction, { visitId: number; instructionId: number; body: OutgoingFluidAdditiveOrder }>({
            query({ visitId, instructionId, body }) {
                return {
                    url: `visit/${visitId}/instruction/${instructionId}/fluid_additive`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, { visitId, instructionId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.InstructionId, id: instructionId },
            ],
        }),
        addOffScheduleAction: builder.mutation<InstructionAction, { visitId: number; body: OutgoingOffSchedAction }>({
            query({ body, visitId }) {
                const { instructionId, note, due_at, value, userId, drawn_amount, administered_amount } = body;

                return {
                    url: `visit/${visitId}/instruction/${instructionId}/action`,
                    method: 'POST',
                    body: {
                        note,
                        due_at,
                        value,
                        status: 'complete',
                        assigned_to: userId,
                        scheduled_time: null,
                        scheduled_by: null,
                        completed_at: null,
                        drawn_amount,
                        administered_amount,
                    },
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Estimate, id: visitId },
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        submitIHDOrder: builder.mutation<
            { instruction_id: number; due_at: number }[],
            { actions: { instruction_id: number; due_at: number }[] }
        >({
            query({ actions }) {
                return {
                    url: `idexx/ihd/order`,
                    method: 'POST',
                    body: actions,
                };
            },
        }),
        submitIHDConfirmation: builder.mutation<
            { id: number; action_id: number; status: string; ui_url: string; ref_lab_pdf_url: string | null },
            { actions: { instruction_id: number; due_at: number }[] }
        >({
            query({ actions }) {
                return {
                    url: `idexx/ihd/confirm`,
                    method: 'POST',
                    body: actions,
                };
            },
        }),
        submitIdexxRefLabConfirmation: builder.mutation<void, { visitId: number; orderId: string; }>({
            query({ visitId, orderId }) {
                return {
                    url: `visit/${visitId}/idexx/ref_lab/confirm`,
                    method: 'POST',
                    body: { order_id: orderId },
                };
            },
        }),
        submitXRayOrder: builder.mutation<ExistingDiagInstruction, { instruction_id: number; due_at: number }>({
            query({ instruction_id, due_at }) {
                return {
                    url: 'webpacs/order',
                    method: 'POST',
                    body: { instruction_id, due_at },
                };
            },
        }),
        submitXRayGenerateLink: builder.mutation<ExistingDiagInstruction, { instruction_id: number; due_at: number }>({
            query({ instruction_id, due_at }) {
                return {
                    url: 'webpacs/generate_link',
                    method: 'POST',
                    body: { instruction_id, due_at },
                };
            },
        }),
        submitIHDResultAssignment: builder.mutation<
            void,
            {
                visitId: number;
                actionId: number;
                resultId: string;
                completedAt: number;
            }
        >({
            query({ actionId, resultId, completedAt }) {
                return {
                    url: 'idexx/ihd/assign',
                    method: 'POST',
                    body: {
                        action_id: actionId,
                        result_id: resultId,
                        completed_at: completedAt,
                    },
                };
            },
            invalidatesTags: (result, error, { visitId }) => [{ type: TagType.Instruction, id: visitId }],
        }),
        submitXRayResultAssignment: builder.mutation<
            void,
            {
                visitId: number;
                actionId: number;
                resultId: string;
                completedAt: number;
            }
        >({
            query({ actionId, resultId, completedAt }) {
                return {
                    url: 'webpacs/assign',
                    method: 'POST',
                    body: {
                        action_id: actionId,
                        result_id: resultId,
                        completed_at: completedAt,
                    },
                };
            },
            invalidatesTags: (result, error, { visitId }) => [{ type: TagType.Instruction, id: visitId }],
        }),

        /**
         * PUT/MODIFY
         */
        modifyAction: builder.mutation<InstructionAction, { visitId: number; body: OutgoingAction }>({
            query({ body, visitId }) {
                const {
                    instructionId,
                    note,
                    status,
                    value,
                    assigned_to,
                    due_at,
                    scheduled_time,
                    scheduled_by,
                    completed_at,
                    drawn_amount,
                    administered_amount,
                } = body;
                const url = `visit/${visitId}/instruction/${instructionId}/action`;
                if (value instanceof File) {
                    const formData = new FormData();
                    formData.append('status', status);
                    formData.append('note', String(!!note ? note : ''));
                    formData.append('value', value);
                    formData.append('assigned_to', String(assigned_to));
                    formData.append('due_at', String(due_at));
                    formData.append('scheduled_time', String(scheduled_time));
                    formData.append('scheduled_by', String(scheduled_by));
                    formData.append('completed_at', String(completed_at));
                    formData.append('drawn_amount', String(drawn_amount));
                    formData.append('administered_amount', String(administered_amount));
                    return {
                        url: url,
                        method: 'POST',
                        headers: { 'Content-Type': 'multipart/form-data' },
                        body: formData,
                    };
                } else {
                    const formattedReturn: ActionUpdate = {
                        status,
                        note: note || null,
                        value: value || null,
                        assigned_to: assigned_to || null,
                        due_at,
                        scheduled_time: scheduled_time || null,
                        scheduled_by: scheduled_by || null,
                        completed_at: completed_at || null,
                        drawn_amount: drawn_amount === 0 ? 0 : drawn_amount || null,
                        administered_amount: administered_amount === 0 ? 0 : administered_amount || null,
                    };
                    return {
                        url: url,
                        method: 'POST',
                        body: formattedReturn,
                        formData: value,
                    };
                }
            },
            transformResponse: (response: { data: InstructionAction }) => {
                return response.data;
            },
            invalidatesTags: (result, error, { visitId, body: { instructionId } }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.InstructionId, id: instructionId },
                { type: TagType.MainTs },
                { type: TagType.Estimate, id: visitId },
            ],
        }),

        modifyFrequency: builder.mutation<
            null,
            { visitId: number; instructionId: number; instructionType: INSTRUCTION_TYPES; body: FrequencyUpdate }
        >({
            query({ body, visitId, instructionType, instructionId }) {
                const instructionRouteMapping = {
                    T: 'task',
                    D: 'diagnostic',
                    M: 'medication',
                    P: 'package',
                    C: 'cri',
                    F: 'fluid',
                    TGH: 'togo_home_med',
                    N: 'non_medical',
                    OT: 'oxygen_therapy',
                };
                const instructionRoute = instructionRouteMapping[instructionType];
                const url = `visit/${visitId}/instruction/${instructionId}/${instructionRoute}`;
                return {
                    url: url,
                    method: 'PUT',
                    body,
                };
            },
            transformResponse: (response: { data: null }) => {
                return response.data;
            },
            invalidatesTags: (result, error, { visitId, instructionId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.InstructionId, id: instructionId },
                { type: TagType.MainTs },
                { type: TagType.Estimate, id: visitId },
            ],
        }),

        modifyMultipleFrequency: builder.mutation<
            null,
            { visitId: number; body: FrequencyUpdateMultiple }
        >({
            query({ body, visitId }) {
                const url = `visit/${visitId}/update_multiple_frequency`;
                return {
                    url: url,
                    method: 'PUT',
                    body,
                };
            },
            transformResponse: (response: { data: null }) => {
                return response.data;
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.MainTs },
                { type: TagType.Estimate, id: visitId },
            ],
        }),

        hideEstimateItem: builder.mutation<null, { visitId: number; instructionId: number }>({
            query({ visitId, instructionId }) {
                return {
                    url: `/visit/${visitId}/instruction/${instructionId}/is_shown_instruction`,
                    method: 'PUT',
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        hideEstimateItemNonMed: builder.mutation<null, { visitId: number; nonMedOrderId: number }>({
            query({ visitId, nonMedOrderId }) {
                return {
                    url: `/visit/${visitId}/non_medical/${nonMedOrderId}/is_shown_non_med`,
                    method: 'PUT',
                };
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        /**
         * DELETE/REMOVE/DISCONTINUE
         */
        discontinueInstruction: builder.mutation<
            ExpandedPatient,
            {
                visitId: number;
                instructionId: number;
                on_behalf_of: number;
            }
        >({
            query({ visitId, instructionId, on_behalf_of }) {
                return {
                    url: `visit/${visitId}/instruction/${instructionId}?op=discontinue`,
                    method: 'PUT',
                    body: { on_behalf_of },
                };
            },
            invalidatesTags: (result, error, { visitId, instructionId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        dischargePatientInstructions: builder.mutation<
            { data: null, linked_actions: any[] },
            {
                visitId: number;
                ids: number[];
                reason?: string;
            }
        >({
            query({ visitId, ids, reason }) {
                return {
                    url: `visit/${visitId}/discharged`,
                    method: 'PUT',
                    body: { ids, on_behalf_of: 0, reason: reason ?? null },
                };
            },
            transformResponse: (response: { data: null, linked_actions: any[] }) => {
                return {data: response.data, linked_actions: response.linked_actions};
            },
            invalidatesTags: (result, error, { visitId }) => [
                { type: TagType.Visit, id: visitId },
                { type: TagType.Instruction, id: visitId },
                { type: TagType.Estimate, id: visitId },
            ],
        }),
        discontinueFluidAdditive: builder.mutation<
            null,
            {
                visitId: number;
                instructionId: number;
                fluidAdditiveId: number;
            }
        >({
            query({ visitId, instructionId, fluidAdditiveId }) {
                return {
                    url: `visit/${visitId}/instruction/${instructionId}/fluid_additive?op=discontinue`,
                    method: 'PUT',
                    body: { fluid_additive_id: fluidAdditiveId },
                };
            },
            invalidatesTags: (result, error, { visitId, instructionId }) => [
                { type: TagType.Instruction, id: visitId },
                { type: TagType.InstructionId, id: instructionId },
            ],
        }),
        getIdexxRefLabs: builder.query<DiagnosticSearchOptionWithRefLabs[], void>({
            query: () => `idexx-ref-labs`,
            transformResponse: (response: { data: DiagnosticSearchOptionWithRefLabs[] }): DiagnosticSearchOptionWithRefLabs[] => {
                return response.data;
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
    //GET
    useGetMainTreatmentSheetQuery,
    useGetInstructionsByVisitIdQuery,
    useLazyGetInstructionsByVisitIdQuery,
    useGetInstructionOptionsQuery,
    useGetFluidOptionsQuery,
    useGetPackageDetailsQuery,
    useDownloadIdexxResultFileQuery,
    useGetMedicationRoutesQuery,
    useGetNonMedicalWhyTypesQuery,
    useGetIndividualInstructionQuery,
    useLazyGetIdexxRecentResultsQuery,
    useLazyGetWebpacsRecentResultsQuery,
    useGetAdditiveOptionsQuery,
    useGetIdexxRefLabsQuery,

    //CREATE
    usePostCheckinTprInstructionsMutation,
    useSetInitialWeightMutation,
    useAddMedInstructionMutation,
    useAddDiagInstructionMutation,
    useAddTaskInstructionMutation,
    useAddCriInstructionMutation,
    useAddFluidInstructionMutation,
    useAddOxygenTherapyInstructionMutation,
    useAddFluidAdditiveMutation,
    useAddOffScheduleActionMutation,
    useSubmitIHDOrderMutation,
    useSubmitIHDConfirmationMutation,
    useSubmitIdexxRefLabConfirmationMutation,
    useSubmitXRayOrderMutation,
    useSubmitXRayGenerateLinkMutation,
    useSubmitIHDResultAssignmentMutation,
    useSubmitXRayResultAssignmentMutation,

    //UPDATE
    useModifyActionMutation,
    useHideEstimateItemMutation,
    useHideEstimateItemNonMedMutation,
    useModifyFrequencyMutation,
    useModifyMultipleFrequencyMutation,

    //DELETE
    useDiscontinueInstructionMutation,
    useDischargePatientInstructionsMutation,
    useDiscontinueFluidAdditiveMutation,
} = instructionService;
